/**
 * Metas e previsão (spec 02 §4.9 · telas 3.8 e 3.9 · passo 4.2b).
 *
 * Montantes de saída admitem null quando a permissão financeira os restringe;
 * percentuais/probabilidades e identificação continuam disponíveis (DS-486).
 *
 * ## O que NÃO existe aqui
 *
 * - **`realizado` não é campo de entrada.** Ele é a soma das vendas do período pelo
 *   calendário da meta, e volta só na saída do atingimento.
 * - **Não existe meta MENSAL guardada.** A meta é trimestral (spec 3.8); o número que
 *   a tela mostra num mês é PRO-RATA, calculado pela fração de dias do trimestre que
 *   o período cobre.
 * - **Não existe cenário guardado.** A escada Garantido ⊆ Provável ⊆ Melhor caso é
 *   calculada sobre `negocio.categoria_previsao` a cada consulta (R-01), e vem pronta
 *   da API — a tela nunca a recalcula.
 */
import { z } from 'zod'
import { codigoDeMoeda, dataNominal, uuid } from './comum.js'
import { referenciaDeFicha } from './referencias.js'

/** Os sete eixos que a v1 expõe (spec 3.8). O modelo comporta mais (02 §4.9). */
export const eixoDaMeta = z.enum(['geral', 'executivo', 'parceiro', 'origem', 'produto', 'praca', 'setor'])
export type EixoDaMeta = z.infer<typeof eixoDaMeta>

/** GLOSSARIO: os três calendários de receita nunca se misturam. A v1 usa `vendas`. */
export const calendarioDaMeta = z.enum(['vendas', 'faturamento', 'recebimento'])

/*
 * `metaComercial` e não `meta`: `comum.ts` já exporta um `meta` — o bloco de paginação
 * de toda lista (spec 03 §1.2). Dois `meta` no mesmo índice é ambiguidade de módulo, e
 * o nome completo diz melhor o que este é.
 */
export const metaComercial = z.object({
  id: uuid,
  eixo: eixoDaMeta,
  eixoRefId: uuid.nullable(),
  eixoSetor: z.enum(['Público', 'Privado']).nullable(),
  /** O nome do alvo, resolvido — a tela não precisa saber de qual tabela ele veio. */
  alvoNome: z.string().nullable(),
  /**
   * A ficha do alvo, só no eixo parceiro (D-136, DS-538): `eixoRefId` é a empresa OU o
   * contato, sem o tipo, e a tela não sabia qual ficha abrir. Nos outros eixos o alvo é
   * usuário interno ou catálogo, sem ficha, e a referência é nula.
   */
  alvoFicha: referenciaDeFicha.nullable(),
  periodoTipo: z.enum(['mes', 'trimestre', 'ano']),
  periodoInicio: dataNominal,
  periodoFim: dataNominal,
  valorMeta: z.string().nullable(),
  moeda: codigoDeMoeda,
  calendario: calendarioDaMeta,
})
export type Meta = z.infer<typeof metaComercial>

/** Uma linha do painel de atingimento: um alvo do eixo escolhido. */
export const linhaDeAtingimento = z.object({
  chave: z.string(),
  rotulo: z.string(),
  /** A foto do executivo (PK-04): só no eixo `executivo`; nos outros eixos, sempre nula. */
  avatarAnexoId: uuid.nullable(),
  /**
   * A ficha do alvo, só no eixo parceiro (D-136, DS-538): a `chave` é
   * `coalesce(empresa_id, contato_id)`, sem o tipo. Nula nos outros eixos e na linha residual.
   */
  ficha: referenciaDeFicha.nullable(),
  /** A meta PRO-RATA do período — não a meta trimestral cheia. */
  metaDoPeriodo: z.string().nullable(),
  realizado: z.string().nullable(),
  /** Fração: 0,82 = 82%. `null` quando não há meta (linha só com venda). */
  atingimento: z.string().nullable(),
  gap: z.string().nullable(),
})
export type LinhaDeAtingimento = z.infer<typeof linhaDeAtingimento>

export const atingimento = z.object({
  eixo: eixoDaMeta,
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  /** Σ das metas pro-rata do recorte. */
  metaTotal: z.string().nullable(),
  realizado: z.string().nullable(),
  atingimentoPct: z.string().nullable(),
  gap: z.string().nullable(),
  /** realizado ÷ dias decorridos até hoje. `null` quando o período ainda não começou. */
  ritmoDiario: z.string().nullable(),
  /** ritmo × dias do período. `null` pelo mesmo motivo. */
  projecao: z.string().nullable(),
  linhas: z.array(linhaDeAtingimento),
  /** A série mensal acumulada — realizado × meta, para o gráfico da 3.8. */
  serie: z.array(z.object({
    mes: z.string(),
    realizado: z.string().nullable(),
    meta: z.string().nullable(),
  })),
})
export type Atingimento = z.infer<typeof atingimento>

/**
 * Os três cenários, CUMULATIVOS (R-01).
 *
 * A escada é sempre não-decrescente: Garantido ⊆ Provável ⊆ Melhor caso. Negócios
 * "Não definido" ficam FORA de todos (D-40) — e por isso aparecem no `higiene`.
 */
export const previsao = z.object({
  periodoDe: dataNominal,
  periodoAte: dataNominal,
  /** Ganhos do período (pela data do ganho) + Σ dos "Garantido". */
  garantido: z.string().nullable(),
  /** Garantido + Σ dos "Provável". */
  provavel: z.string().nullable(),
  /** Provável + Σ dos "Possível". */
  melhorCaso: z.string().nullable(),
  /** Σ dos ganhos do período — a parcela que já é fato dentro do piso. */
  ganhoNoPeriodo: z.string().nullable(),
  /**
   * Σ valor × probabilidade do estágio — a QUARTA leitura, com nome próprio.
   * Nunca se mistura aos cenários (R-01).
   */
  ponderado: z.string().nullable(),
  /** A meta pro-rata do período, do eixo `geral`. `null` quando não há meta. */
  metaDoPeriodo: z.string().nullable(),
  /** Provável − meta. Mesma fórmula da 3.5, e divergência entre as duas é falha. */
  gap: z.string().nullable(),
  /** Os negócios sem categoria — fora dos cenários, dentro da higiene (D-40). */
  higiene: z.array(z.object({ id: uuid, codigo: z.string(), titulo: z.string() })),
  /** A matriz estágio × mês de fechamento, ponderada. */
  matriz: z.array(z.object({
    estagioNome: z.string(),
    probabilidade: z.string(),
    ordem: z.number().int(),
    meses: z.array(z.object({ mes: z.string(), ponderado: z.string().nullable() })),
  })),
})
export type Previsao = z.infer<typeof previsao>

export const entradaDeMeta = z.object({
  eixo: eixoDaMeta,
  eixoRefId: uuid.nullish(),
  eixoSetor: z.enum(['Público', 'Privado']).nullish(),
  periodoInicio: dataNominal,
  valorMeta: z.string().nullable(),
  moeda: codigoDeMoeda.nullish(),
  calendario: calendarioDaMeta.default('vendas'),
})
export type EntradaDeMeta = z.input<typeof entradaDeMeta>

/**
 * O detalhamento de um executivo no período (3.8, expansão da linha).
 *
 * É o único lugar onde o **sub-produto** aparece: o eixo por produto lista PageSignal
 * como UMA linha (D-24), e aqui "PageSignal · Assinatura" volta a ser leitura de item.
 */
export const detalheDoExecutivo = z.object({
  produtos: z.array(z.object({ nome: z.string(), valor: z.string().nullable() })),
  /**
   * O Cliente FATURADO (D-74) — nunca o anunciante —, com a empresa, para o nome abrir a
   * ficha (D-136, DS-538). Nula quando a parte ficou só com o nome congelado.
   */
  clientes: z.array(z.object({ nome: z.string(), valor: z.string().nullable(), empresaId: uuid.nullable() })),
})
export type DetalheDoExecutivo = z.infer<typeof detalheDoExecutivo>
